/**
 * PontajTab - Componenta pentru tab-ul Pontaj
 * Extrasă din AdminDashboard.jsx pentru modularizare și performanță
 */
import { memo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { 
  Clock, Download, FileText, Edit, UserCheck, RefreshCw, Star, Search, X
} from 'lucide-react';

// Constante pentru lunile în română
const MONTHS_RO = [
  'Ianuarie', 'Februarie', 'Martie', 'Aprilie', 'Mai', 'Iunie',
  'Iulie', 'August', 'Septembrie', 'Octombrie', 'Noiembrie', 'Decembrie'
];

/**
 * Componenta PontajTab
 * @param {Object} props
 * @param {number} props.pontajMonth - Luna selectată (1-12)
 * @param {number} props.pontajYear - Anul selectat
 * @param {Object} props.pontajData - Datele de pontaj pentru angajați
 * @param {boolean} props.pontajLoading - Indicator de încărcare
 * @param {boolean} props.pontajEditMode - Modul de editare activ
 * @param {Array} props.drivers - Lista de angajați
 * @param {Function} props.setPontajMonth - Setter pentru luna
 * @param {Function} props.setPontajYear - Setter pentru an
 * @param {Function} props.setPontajEditMode - Setter pentru modul editare
 * @param {Function} props.setImportDialog - Deschide dialogul de import
 * @param {Function} props.setSarbatoriDialog - Deschide dialogul de sărbători
 * @param {Function} props.fetchSarbatori - Funcție pentru încărcarea sărbătorilor
 * @param {Function} props.exportPontajPDF - Export PDF
 * @param {Function} props.exportPontajExcel - Export Excel
 * @param {Function} props.openTicheteMasaExport - Export tichete masă
 * @param {Function} props.cleanupOrphanDocuments - Curățare documente orfane
 * @param {Function} props.handleCellClick - Handler click celulă
 * @param {Function} props.isSarbatoare - Verifică dacă ziua e sărbătoare
 * @param {Function} props.getSarbatoareNume - Obține numele sărbătorii
 * @param {Function} props.hasPermission - Verifică permisiuni
 * @param {boolean} props.loading - Indicator de încărcare general
 */
const PontajTab = memo(function PontajTab({
  pontajMonth,
  pontajYear,
  pontajData,
  pontajLoading,
  pontajEditMode,
  drivers,
  setPontajMonth,
  setPontajYear,
  setPontajEditMode,
  setImportDialog,
  setSarbatoriDialog,
  fetchSarbatori,
  exportPontajPDF,
  exportPontajExcel,
  openTicheteMasaExport,
  cleanupOrphanDocuments,
  handleCellClick,
  isSarbatoare,
  getSarbatoareNume,
  hasPermission,
  loading
}) {
  // State pentru căutare angajat
  const [searchQuery, setSearchQuery] = useState('');
  
  const daysInMonth = new Date(pontajYear, pontajMonth, 0).getDate();
  const today = new Date();
  const isCurrentMonth = pontajYear === today.getFullYear() && pontajMonth === today.getMonth() + 1;
  const isFutureMonth = pontajYear > today.getFullYear() || 
    (pontajYear === today.getFullYear() && pontajMonth > today.getMonth() + 1);

  // Navigare luni
  const goToPrevMonth = () => {
    if (pontajMonth === 1) {
      setPontajMonth(12);
      setPontajYear(pontajYear - 1);
    } else {
      setPontajMonth(pontajMonth - 1);
    }
  };

  const goToNextMonth = () => {
    if (pontajMonth === 12) {
      setPontajMonth(1);
      setPontajYear(pontajYear + 1);
    } else {
      setPontajMonth(pontajMonth + 1);
    }
  };

  const goToCurrentMonth = () => {
    setPontajMonth(new Date().getMonth() + 1);
    setPontajYear(new Date().getFullYear());
  };

  // Filtrare și sortare angajați pentru pontaj
  const pontajEmployees = drivers.filter(d => {
    if (d.role === 'admin') return false;
    if (d.in_pontaj === false) return false;
    
    // Verifică dacă pontajul a expirat
    if (d.pontaj_pana_la) {
      const endDate = new Date(d.pontaj_pana_la);
      const endYear = endDate.getFullYear();
      const endMonth = endDate.getMonth() + 1;
      if (pontajYear > endYear || (pontajYear === endYear && pontajMonth > endMonth)) {
        return false;
      }
    }
    
    // Verifică dacă angajatul nu e încă angajat
    if (d.angajat_de_la) {
      const startDate = new Date(d.angajat_de_la);
      const startYear = startDate.getFullYear();
      const startMonth = startDate.getMonth() + 1;
      if (pontajYear < startYear || (pontajYear === startYear && pontajMonth < startMonth)) {
        return false;
      }
    }
    
    // Filtrare după căutare
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      const fullName = `${d.nume || ''} ${d.prenume || ''}`.toLowerCase();
      const username = (d.username || '').toLowerCase();
      if (!fullName.includes(query) && !username.includes(query)) {
        return false;
      }
    }
    
    return true;
  }).sort((a, b) => {
    const aHasContract = a.numar_contract && a.numar_contract.trim() !== '';
    const bHasContract = b.numar_contract && b.numar_contract.trim() !== '';
    if (aHasContract && !bHasContract) return -1;
    if (!aHasContract && bHasContract) return 1;
    if (!aHasContract && !bHasContract) return 0;
    const aNum = parseInt(a.numar_contract) || 999999;
    const bNum = parseInt(b.numar_contract) || 999999;
    return aNum - bNum;
  });

  // Calculează totalurile pentru un angajat
  const calculateTotals = (driver) => {
    const employeePontaj = pontajData[driver.id] || {};
    
    const hasResignation = driver.pontaj_pana_la && (() => {
      const endDate = new Date(driver.pontaj_pana_la);
      return pontajYear === endDate.getUTCFullYear() && pontajMonth === endDate.getUTCMonth() + 1;
    })();
    const resignationDay = hasResignation ? new Date(driver.pontaj_pana_la).getUTCDate() : null;
    
    const hireDate = driver.angajat_de_la ? new Date(driver.angajat_de_la) : null;
    const hasHireDateThisMonth = hireDate && pontajYear === hireDate.getUTCFullYear() && pontajMonth === hireDate.getUTCMonth() + 1;
    const hireDateDay = hasHireDateThisMonth ? hireDate.getUTCDate() : null;
    const monthBeforeHire = hireDate && (pontajYear < hireDate.getUTCFullYear() || 
      (pontajYear === hireDate.getUTCFullYear() && pontajMonth < hireDate.getUTCMonth() + 1));
    
    let totalOre = 0, totalCO = 0, totalCM = 0, totalNM = 0, totalAltele = 0, totalZL = 0, totalTIK = 0;
    // totalAltele = CFP + CNC + CD
    // totalZL = zile lucrate (inclusiv sâmbăta dacă a lucrat)
    // totalTIK = tichete de masă (doar L-V, fără weekend/sărbătoare/CO/CM etc.)
    
    for (let d = 1; d <= daysInMonth; d++) {
      if (monthBeforeHire) continue;
      if (hasHireDateThisMonth && d < hireDateDay) continue;
      
      const dayDate = new Date(pontajYear, pontajMonth - 1, d);
      const isWeekend = dayDate.getDay() === 0 || dayDate.getDay() === 6;
      const isHoliday = isSarbatoare(d);
      
      const val = employeePontaj[d]?.value;
      
      // Skip zilele viitoare din calcule DOAR dacă nu au date înregistrate
      const todayForTotals = new Date();
      const isFutureDayForTotals = isCurrentMonth && d > todayForTotals.getDate();
      if ((isFutureDayForTotals || isFutureMonth) && !val) continue;
      
      if (hasResignation && d > resignationDay) continue;
      if (isWeekend || isHoliday) {
        // Dacă are ore lucrate explicit în weekend/sărbătoare, le adunăm
        if (val !== undefined && val !== null) {
          const hours = parseInt(val);
          if (!isNaN(hours) && hours > 0) {
            totalOre += hours;
            totalZL++;  // Zi lucrată (și sâmbăta dacă a lucrat)
          }
        }
        continue;
      }
      
      // Dacă nu există valoare explicită, folosim norma implicită pentru zilele de lucru
      if (val === undefined || val === null) {
        // Zi normală de lucru fără înregistrare = orele din normă
        const defaultHours = driver.norma_ore || 8;
        totalOre += defaultHours;
        totalZL++;
        totalTIK++;  // Tichet de masă (zi lucrătoare L-V)
        continue;
      }
      
      const hours = parseInt(val);
      if (!isNaN(hours)) {
        totalOre += hours;
        if (hours > 0) {
          totalZL++;
          totalTIK++;  // Tichet de masă (zi lucrătoare L-V cu ore)
        }
      } else if (val === 'CO') {
        totalCO++;
      } else if (val === 'CM') {
        totalCM++;
      } else if (val === 'CNC' || val === 'CD' || val === 'CFP') {
        totalAltele++;
      } else if (val === 'NM') {
        totalNM++;
      }
    }
    
    return { totalOre, totalCO, totalCM, totalNM, totalAltele, totalZL, totalTIK,
             resignationDay, hireDateDay, monthBeforeHire, hasHireDateThisMonth, hasResignation };
  };

  // Obține valoarea și stilul pentru o celulă
  const getCellData = (driver, day, totals) => {
    const { resignationDay, hireDateDay, monthBeforeHire, hasHireDateThisMonth, hasResignation } = totals;
    const employeePontaj = pontajData[driver.id] || {};
    
    const dayDate = new Date(pontajYear, pontajMonth - 1, day);
    const isWeekend = dayDate.getDay() === 0 || dayDate.getDay() === 6;
    const isHoliday = isSarbatoare(day);
    const dayData = employeePontaj[day];
    const val = dayData?.value;
    // Triple check for document - must have dayData AND has_document must be exactly true
    const hasDoc = dayData !== undefined && dayData !== null && dayData.has_document === true;
    const isPending = !!dayData?.pending;
    const pendingRing = isPending ? ' ring-2 ring-orange-400 ring-inset' : '';
    
    // Verificări speciale
    if (monthBeforeHire || (hasHireDateThisMonth && day < hireDateDay)) {
      return { value: 'X', className: 'bg-slate-100 text-slate-400', hasDoc: false, isDisabled: true };
    }
    
    if (hasResignation && day > resignationDay) {
      return { value: 'X', className: 'bg-slate-100 text-slate-400', hasDoc: false, isDisabled: true };
    }
    
    if (isHoliday) {
      return { 
        value: val || 'X', 
        className: (val ? getCellClassByValue(val) : 'bg-violet-200 text-violet-700') + pendingRing,
        hasDoc,
        isDisabled: false
      };
    }
    
    if (isWeekend) {
      return { 
        value: val || 'X', 
        className: (val ? getCellClassByValue(val) : 'bg-slate-200 text-slate-600') + pendingRing,
        hasDoc,
        isDisabled: false
      };
    }
    
    // Zi normală de lucru
    // Verifică dacă ziua este în viitor
    const todayCheck = new Date();
    const isFutureDay = isCurrentMonth && day > todayCheck.getDate();
    
    // Dacă ziua este în viitor ȘI nu există date înregistrate, nu afișăm ore
    // Dar DACĂ există deja o valoare (CO, CM, etc.), o afișăm!
    // Permitem click pentru a adăuga CO, CM, CFP în zile viitoare
    if ((isFutureDay || isFutureMonth) && !val) {
      return {
        value: '',
        className: 'bg-slate-50 text-slate-300 hover:bg-slate-100 cursor-pointer' + pendingRing,
        hasDoc: false,
        isDisabled: false
      };
    }
    
    // Dacă nu există valoare explicită, afișăm norma implicită
    const defaultHours = driver.norma_ore || 8;
    const displayValue = val !== undefined && val !== null ? val : defaultHours;
    
    return {
      value: displayValue,
      className: getCellClassByValue(displayValue) + pendingRing,
      hasDoc,
      isDisabled: false
    };
  };

  // Clasă CSS bazată pe valoare
  const getCellClassByValue = (val) => {
    if (!val) return 'bg-white';
    const hours = parseInt(val);
    if (!isNaN(hours)) return 'bg-green-100 text-green-800';
    switch (val) {
      case 'CO': return 'bg-blue-100 text-blue-700';
      case 'CM': return 'bg-orange-100 text-orange-700';
      case 'CNC': return 'bg-pink-100 text-pink-700';
      case 'CD': return 'bg-gray-200 text-gray-700';
      case 'NM': return 'bg-red-100 text-red-700';
      case 'CFP': return 'bg-yellow-100 text-yellow-700';
      case 'DEM': return 'bg-purple-100 text-purple-700';
      case 'X': return 'bg-slate-200 text-slate-600';
      default: return 'bg-white';
    }
  };

  const canImport = pontajYear < new Date().getFullYear() || 
    (pontajYear === new Date().getFullYear() && pontajMonth < new Date().getMonth() + 1);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Pontaj Angajați
              </CardTitle>
              <CardDescription>Gestionează pontajul și orele de lucru ale angajaților</CardDescription>
            </div>
            
            {/* Month/Year Navigation */}
            <div className="flex items-center gap-2 flex-wrap">
              <Button variant="outline" size="sm" onClick={goToPrevMonth}>
                ← Luna anterioară
              </Button>
              
              <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-lg border border-blue-200">
                <Select value={String(pontajMonth)} onValueChange={(v) => setPontajMonth(parseInt(v))}>
                  <SelectTrigger className="w-[130px] border-0 bg-transparent">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MONTHS_RO.map((month, idx) => (
                      <SelectItem key={idx + 1} value={String(idx + 1)}>{month}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={String(pontajYear)} onValueChange={(v) => setPontajYear(parseInt(v))}>
                  <SelectTrigger className="w-[90px] border-0 bg-transparent">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[2024, 2025, 2026].map(y => (
                      <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <Button variant="outline" size="sm" onClick={goToNextMonth}>
                Luna următoare →
              </Button>
              
              <Button variant="default" size="sm" onClick={goToCurrentMonth}>
                Luna curentă
              </Button>
              
              {/* Export buttons */}
              {hasPermission('pontaj.export') && (
                <>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={exportPontajPDF}
                    disabled={loading}
                    className="bg-red-50 hover:bg-red-100 text-red-700 border-red-200"
                  >
                    <Download className="w-4 h-4 mr-1" />
                    PDF
                  </Button>
                  
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={exportPontajExcel}
                    disabled={loading}
                    className="bg-green-50 hover:bg-green-100 text-green-700 border-green-200"
                  >
                    <Download className="w-4 h-4 mr-1" />
                    Excel
                  </Button>
                  
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={openTicheteMasaExport}
                    disabled={loading}
                    className="bg-purple-50 hover:bg-purple-100 text-purple-700 border-purple-200"
                  >
                    <Download className="w-4 h-4 mr-1" />
                    Tichete Masă
                  </Button>
                </>
              )}
              
              {/* Import button */}
              {hasPermission('import.excel') && canImport && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setImportDialog(true)}
                  disabled={loading}
                  className="bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200"
                >
                  <FileText className="w-4 h-4 mr-1" />
                  Import Excel
                </Button>
              )}
              
              {/* Edit mode toggle */}
              {hasPermission('pontaj.editare') && (
                <Button 
                  data-testid="pontaj-edit-mode-toggle"
                  variant={pontajEditMode ? "default" : "outline"}
                  size="sm"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setPontajEditMode(prev => !prev);
                  }}
                  type="button"
                  className={pontajEditMode 
                    ? "bg-green-600 hover:bg-green-700 text-white" 
                    : "bg-amber-50 hover:bg-amber-100 text-amber-700 border-amber-200"
                  }
                >
                  {pontajEditMode ? (
                    <span className="flex items-center">
                      <UserCheck className="w-4 h-4 mr-1" />
                      <span>Finalizează editarea</span>
                    </span>
                  ) : (
                    <span className="flex items-center">
                      <Edit className="w-4 h-4 mr-1" />
                      <span>Editare pontaj</span>
                    </span>
                  )}
                </Button>
              )}
              
              {/* Cleanup button */}
              {hasPermission('pontaj.editare') && pontajEditMode && (
                <Button 
                  variant="outline"
                  size="sm"
                  onClick={cleanupOrphanDocuments}
                  disabled={loading}
                  className="bg-slate-50 hover:bg-slate-100 text-slate-700 border-slate-200"
                  title="Curăță documentele orfane"
                >
                  <RefreshCw className="w-4 h-4 mr-1" />
                  Repară documente
                </Button>
              )}
              
              {/* Holidays button */}
              <Button 
                variant="outline"
                size="sm"
                onClick={() => {
                  fetchSarbatori();
                  setSarbatoriDialog(true);
                }}
                className="bg-violet-50 hover:bg-violet-100 text-violet-700 border-violet-200"
              >
                <Star className="w-4 h-4 mr-1" />
                Sărbători legale
              </Button>
            </div>
          </div>
        </CardHeader>
        
        <CardContent>
          {/* Search Bar */}
          <div className="mb-4 flex items-center gap-2">
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                type="text"
                placeholder="Caută angajat..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-8"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            {searchQuery && (
              <span className="text-sm text-slate-500">
                {pontajEmployees.length} rezultat{pontajEmployees.length !== 1 ? 'e' : ''}
              </span>
            )}
          </div>
          
          {/* Edit Mode Indicator */}
          {pontajEditMode && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2 text-amber-800">
              <Edit className="w-4 h-4" />
              <span className="font-medium">Mod editare activ</span>
              <span className="text-sm">- Click pe orice celulă pentru a modifica sau încărca documente</span>
            </div>
          )}
          
          {/* Legend */}
          <div className="flex flex-wrap gap-4 mb-4 p-3 bg-slate-50 rounded-lg text-xs">
            <div className="flex items-center gap-1">
              <span className="w-6 h-5 bg-green-100 border border-green-300 rounded text-center text-[10px] leading-5">2/4/6/8</span>
              <span>Ore lucrate</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="w-6 h-5 bg-blue-100 border border-blue-300 rounded text-center text-[10px] leading-5 text-blue-700">CO</span>
              <span>Concediu Odihnă</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="w-6 h-5 bg-orange-100 border border-orange-300 rounded text-center text-[10px] leading-5 text-orange-700">CM</span>
              <span>Concediu Medical</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="w-6 h-5 bg-slate-200 border border-slate-400 rounded text-center text-[10px] leading-5 text-slate-600">X</span>
              <span>Weekend</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="w-6 h-5 bg-violet-200 border border-violet-400 rounded text-center text-[10px] leading-5 text-violet-700">X</span>
              <span>Sărbătoare legală</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="w-6 h-5 bg-red-100 border border-red-300 rounded text-center text-[10px] leading-5 text-red-700">NM</span>
              <span>Absență Nemotivată</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="w-6 h-5 bg-purple-100 border border-purple-300 rounded text-center text-[10px] leading-5 text-purple-700">DEM</span>
              <span>Demisie</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
              <span>Document atașat</span>
            </div>
          </div>
          
          {/* Loading state */}
          {pontajLoading ? (
            <div className="text-center py-8 text-slate-500">
              <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-2"></div>
              <p>Se încarcă pontajul...</p>
            </div>
          ) : (
            /* Pontaj Table */
            <div className="overflow-x-auto border rounded-lg">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-slate-100">
                    <th className="sticky left-0 bg-slate-100 z-10 px-2 py-2 text-left font-semibold border-r w-8">Nr.</th>
                    <th className="sticky left-8 bg-slate-100 z-10 px-2 py-2 text-left font-semibold border-r min-w-[180px]">Nume și Prenume</th>
                    {/* Day columns */}
                    {Array.from({ length: daysInMonth }, (_, i) => {
                      const dayDate = new Date(pontajYear, pontajMonth - 1, i + 1);
                      const isWeekend = dayDate.getDay() === 0 || dayDate.getDay() === 6;
                      const isHoliday = isSarbatoare(i + 1);
                      const holidayName = getSarbatoareNume(i + 1);
                      const dayNames = ['D', 'L', 'Ma', 'Mi', 'J', 'V', 'S'];
                      return (
                        <th 
                          key={i} 
                          className={`px-1 py-1 text-center font-medium border-r min-w-[32px] ${
                            isHoliday ? 'bg-violet-300 text-violet-800' : isWeekend ? 'bg-slate-300 text-slate-600' : 'bg-slate-100'
                          }`}
                          title={isHoliday ? holidayName : ''}
                        >
                          <div className="text-[10px] text-slate-500">{dayNames[dayDate.getDay()]}</div>
                          <div>{i + 1}</div>
                        </th>
                      );
                    })}
                    <th className="px-2 py-2 text-center font-semibold bg-green-100 border-l-2 border-green-300 min-w-[50px]">Total Ore</th>
                    <th className="px-2 py-2 text-center font-semibold bg-blue-100 min-w-[40px]">CO</th>
                    <th className="px-2 py-2 text-center font-semibold bg-orange-100 min-w-[40px]">CM</th>
                    <th className="px-2 py-2 text-center font-semibold bg-red-100 min-w-[40px]">NM</th>
                    <th className="px-2 py-2 text-center font-semibold bg-yellow-100 min-w-[60px]" title="CFP/CNC/CD">Altele</th>
                    <th className="px-2 py-2 text-center font-semibold bg-purple-100 min-w-[40px]" title="Zile Lucrate" data-testid="pontaj-header-zl">ZL</th>
                    <th className="px-2 py-2 text-center font-semibold bg-teal-100 min-w-[40px]" title="Tichete de Masă" data-testid="pontaj-header-tik">TIK</th>
                  </tr>
                </thead>
                <tbody>
                  {pontajEmployees.length === 0 ? (
                    <tr>
                      <td colSpan={daysInMonth + 9} className="text-center py-8 text-slate-500">
                        Nu există angajați de afișat în pontaj pentru această lună
                      </td>
                    </tr>
                  ) : (
                    pontajEmployees.map((driver, idx) => {
                      const totals = calculateTotals(driver);
                      const displayName = driver.nume && driver.prenume 
                        ? `${driver.nume} ${driver.prenume}` 
                        : driver.username;
                      
                      return (
                        <tr key={driver.id} className="border-b hover:bg-slate-50" data-testid={`pontaj-row-${driver.id}`}>
                          <td className="sticky left-0 bg-white z-10 px-2 py-1.5 text-center border-r font-medium text-slate-500">
                            {idx + 1}
                          </td>
                          <td className="sticky left-8 bg-white z-10 px-2 py-1.5 border-r font-medium">
                            {displayName}
                          </td>
                          {/* Day cells */}
                          {Array.from({ length: daysInMonth }, (_, i) => {
                            const day = i + 1;
                            const cellData = getCellData(driver, day, totals);
                            const employeePontaj = pontajData[driver.id] || {};
                            
                            return (
                              <td 
                                key={i}
                                data-testid={`pontaj-cell-${driver.id}-${day}`}
                                className={`px-1 py-1 text-center border-r cursor-pointer hover:ring-2 hover:ring-blue-400 hover:ring-inset transition-all relative overflow-hidden ${cellData.className}`}
                                onClick={() => !cellData.isDisabled && handleCellClick(driver, day, employeePontaj[day]?.value)}
                              >
                                {cellData.value}
                                {cellData.hasDoc && (
                                  <span className="absolute top-0.5 right-0.5 w-2 h-2 bg-blue-500 rounded-full"></span>
                                )}
                              </td>
                            );
                          })}
                          {/* Totals */}
                          <td className="px-2 py-1.5 text-center font-semibold bg-green-50 text-green-800 border-l-2 border-green-300">
                            {totals.totalOre || '-'}
                          </td>
                          <td className="px-2 py-1.5 text-center font-semibold bg-blue-50 text-blue-700">
                            {totals.totalCO || '-'}
                          </td>
                          <td className="px-2 py-1.5 text-center font-semibold bg-orange-50 text-orange-700">
                            {totals.totalCM || '-'}
                          </td>
                          <td className="px-2 py-1.5 text-center font-semibold bg-red-50 text-red-700">
                            {totals.totalNM || '-'}
                          </td>
                          <td className="px-2 py-1.5 text-center font-semibold bg-yellow-50 text-yellow-700">
                            {totals.totalAltele || '-'}
                          </td>
                          <td className="px-2 py-1.5 text-center font-semibold bg-purple-50 text-purple-700" title="Zile Lucrate" data-testid={`pontaj-zl-${driver.id}`}>
                            {totals.totalZL || '-'}
                          </td>
                          <td className="px-2 py-1.5 text-center font-semibold bg-teal-50 text-teal-700" title="Tichete de Masă" data-testid={`pontaj-tik-${driver.id}`}>
                            {totals.totalTIK || '-'}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
                {pontajEmployees.length > 0 && (
                  <tfoot>
                    <tr className="border-t-2 border-slate-300 bg-slate-100">
                      <td className="sticky left-0 bg-slate-100 z-10 px-2 py-2 text-center font-bold text-slate-600 border-r" />
                      <td className="sticky left-8 bg-slate-100 z-10 px-2 py-2 border-r font-bold text-slate-700">TOTAL</td>
                      {Array.from({ length: daysInMonth }, (_, i) => <td key={i} className="border-r" />)}
                      <td className="px-2 py-2 text-center font-bold bg-green-100 text-green-800 border-l-2 border-green-300" />
                      <td className="px-2 py-2 text-center font-bold bg-blue-200 text-blue-900" data-testid="total-co-luna">
                        {pontajEmployees.reduce((sum, d) => sum + (calculateTotals(d).totalCO || 0), 0) || '-'}
                      </td>
                      <td className="px-2 py-2 text-center bg-orange-100" />
                      <td className="px-2 py-2 text-center bg-red-100" />
                      <td className="px-2 py-2 text-center bg-yellow-100" />
                      <td className="px-2 py-2 text-center bg-purple-100" />
                      <td className="px-2 py-2 text-center bg-teal-100" />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}
          
          {/* Info text */}
          <div className="mt-4 text-xs text-slate-500 flex items-center gap-2">
            <span>💡</span>
            <span>
              {pontajEditMode 
                ? 'Mod editare activ. Click pe orice celulă pentru a modifica sau încărca documente.' 
                : 'Click pe o celulă cu document (bulină albastră) pentru a-l vizualiza. Pentru a edita, apasă butonul "Editare pontaj".'}
            </span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
});

export default PontajTab;
